/**
 * Tag Input Component
 * 
 * A reusable component for managing tags with inline add/remove functionality.
 * Users can add tags through an input field with an inline add button and remove them via badge buttons.
 */

import * as React from "react"
import { Plus, X } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Label } from "./label"
import { Badge } from "./badge"
import { toast } from "../../hooks/use-sonner-toast"

export interface TagInputItem {
  id: string
  value: string
}

export interface TagInputProps {
  /** Array of tags */
  tags: TagInputItem[]
  /** Callback when tags are updated */
  onTagsChange: (tags: TagInputItem[]) => void
  /** Label for the input section */
  label?: string
  /** Description text below the label */
  description?: string
  /** Placeholder text for the input field */
  placeholder?: string
  /** Text to show when no tags are added */
  emptyStateText?: string
  /** Text to show when tags are present (prefix) */
  tagsLabelText?: string
  /** Minimum number of tags required */
  minTags?: number
  /** Maximum number of tags allowed */
  maxTags?: number
  /** Custom validation function for tag values */
  validateTag?: (value: string) => boolean | string
  /** Custom className for the container */
  className?: string
  /** Custom className for the input field */
  inputClassName?: string
  /** Custom className for the add button */
  addButtonClassName?: string
  /** Custom className for the tags container */
  tagsClassName?: string
  /** Custom className for individual tag badges */
  tagClassName?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Auto-complete attribute for the input */
  autoComplete?: string
  /** Name attribute for the input */
  name?: string
  /** ID for the input field */
  id?: string
  /** Allow duplicate tags */
  allowDuplicates?: boolean
  /** Transform tag value before adding (e.g., toLowerCase, trim) */
  transformValue?: (value: string) => string
}

const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  ({
    tags = [],
    onTagsChange,
    label,
    description,
    placeholder = "Add tag",
    emptyStateText = "No tags added yet. Enter tags above.",
    tagsLabelText = "Tags:",
    minTags = 0,
    maxTags,
    validateTag,
    className,
    inputClassName,
    addButtonClassName,
    tagsClassName,
    tagClassName,
    disabled = false,
    autoComplete = "on",
    name,
    id,
    allowDuplicates = false,
    transformValue,
    ...props
  }, ref) => {
    const [inputValue, setInputValue] = React.useState("")
    const [validationError, setValidationError] = React.useState<string | null>(null)

    const handleAddTag = React.useCallback(() => {
      if (!inputValue.trim()) return
      
      // Check max tags limit
      if (maxTags && tags.length >= maxTags) {
        toast({
          variant: "destructive",
          title: "Maximum tags reached",
          description: `You can only add up to ${maxTags} tags.`,
        })
        return
      }

      let processedValue = inputValue.trim()
      
      // Apply value transformation if provided
      if (transformValue) {
        processedValue = transformValue(processedValue)
      }

      // Check for duplicates if not allowed
      if (!allowDuplicates && tags.some(tag => tag.value.toLowerCase() === processedValue.toLowerCase())) {
        toast({
          variant: "destructive",
          title: "Duplicate tag",
          description: "This tag already exists.",
        })
        return
      }

      // Validate tag if validator is provided
      if (validateTag) {
        const validationResult = validateTag(processedValue)
        if (validationResult !== true) {
          const errorMessage = typeof validationResult === 'string' ? validationResult : 'Invalid tag'
          toast({
            variant: "destructive",
            title: "Invalid tag",
            description: errorMessage,
          })
          return
        }
      }

      // Clear any previous validation errors
      setValidationError(null)

      // Add new tag
      const newTag: TagInputItem = {
        id: `${Date.now()}-${Math.random()}`,
        value: processedValue
      }

      onTagsChange([...tags, newTag])
      setInputValue("")
    }, [inputValue, tags, maxTags, validateTag, onTagsChange, allowDuplicates, transformValue])

    const handleRemoveTag = React.useCallback((tagId: string) => {
      // Always allow removal - no minTags restriction in UI
      const updatedTags = tags.filter(tag => tag.id !== tagId)
      onTagsChange(updatedTags)
    }, [tags, onTagsChange])

    const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      // Clear validation error when user starts typing
      if (validationError) {
        setValidationError(null)
      }
    }, [validationError])

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddTag()
      }
    }, [handleAddTag])

    const canAddMore = !maxTags || tags.length < maxTags

    return (
      <div ref={ref} className={cn("space-y-4", className)} {...props}>
        {label && (
          <Label htmlFor={id} className="text-base font-semibold">
            {label}
          </Label>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
        )}
        
        <div className="space-y-3">
          {/* Input field with inline add button */}
          <div className="space-y-2">
            <div className="relative flex items-center">
              <Input
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={cn("pr-10", inputClassName)}
                autoComplete={autoComplete}
                name={name}
                id={id}
                disabled={disabled || !canAddMore}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute right-1 h-8 w-8 bg-gray-100 hover:bg-gray-200",
                  addButtonClassName
                )}
                onClick={handleAddTag}
                disabled={disabled || !inputValue.trim() || !canAddMore}
              >
                <Plus className="h-4 w-4" />
                <span className="sr-only">Add tag</span>
              </Button>
            </div>
            {validationError && (
              <p className="text-sm text-destructive">{validationError}</p>
            )}
          </div>

          {/* Tags preview */}
          <div className="mt-2">
            <p className="text-sm text-muted-foreground mb-2">
              {tags.length > 0 ? tagsLabelText : emptyStateText}
            </p>
            <div className={cn("flex flex-wrap gap-2", tagsClassName)}>
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="customGray"
                  className={cn(
                    "px-2 py-1 h-7 flex items-center gap-1 border border-gray-300 text-black bg-gray-50 hover:bg-gray-100",
                    tagClassName
                  )}
                >
                  {tag.value}
                  <button
                    type="button"
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-gray-200"
                    onClick={() => handleRemoveTag(tag.id)}
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Remove {tag.value}</span>
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Helper text for limits */}
        {(minTags > 0 || maxTags) && (
          <p className="text-xs text-muted-foreground">
            {minTags > 0 && maxTags && (
              `${tags.length}/${maxTags} tags (minimum ${minTags} required)`
            )}
            {minTags > 0 && !maxTags && (
              `${tags.length} tags (minimum ${minTags} required)`
            )}
            {!minTags && maxTags && (
              `${tags.length}/${maxTags} tags`
            )}
          </p>
        )}
      </div>
    )
  }
)

TagInput.displayName = "TagInput"

export { TagInput }